import { useSteps, useToast } from '@chakra-ui/react';
import { createContext, useEffect, useState, useCallback, useRef } from 'react';
import { fetchPatient } from '../api/patients';
import { getUser } from '../cookie/user';
import axios from 'axios';

export const CartContext = createContext({});

export const CartProvider = ({ children }) => {
  const user = getUser();
  const isFirstMount = useRef(true);
  const skipSync = useRef(false);

  // INITIALIZE FROM LOCALSTORAGE
  const [cartPlans, setCartPlans] = useState(() => {
    const saved = localStorage.getItem('cartPlans');
    return saved ? JSON.parse(saved) : [];
  });
  
  const [cartData, setCartData] = useState([]);
  const [patients, setPatients] = useState([]);
  const [availablePatients, setAvailablePatients] = useState([]);
  const [addPatientsDrawer, setAddPatientsDrawer] = useState(false);
  const [selectedItem, setSelectedItem] = useState();
  const [location, setLocation] = useState();
  const [orderDetail, setOrderDetail] = useState(() => {
    const saved = localStorage.getItem('orderDetail');
    return saved ? JSON.parse(saved) : {
      appointmentDate: '',
      appointmentTime: '',
      phone: '',
      email: '',
      mainAddress: '',
      address: {},
      sampleMode: '',
    };
  });
  const [isPaymentMethodConfirmed, setIsPaymentMethodConfirmed] = useState(false);
  const [triggerOrderPlacement, setTriggerOrderPlacement] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(() => {
    const saved = localStorage.getItem('orderSuccess');
    return saved === 'true';
  });
  const [paymentStatus, setPaymentStatus] = useState(() => {
    const saved = localStorage.getItem('paymentStatus');
    return saved || 'pending';
  }); // pending, confirming, success
  const toast = useToast();
  const [sessionOrderId, setSessionOrderId] = useState(() => {
    return localStorage.getItem('sessionOrderId') || null;
  });
  const [bookingStatus, setBookingStatus] = useState('idle'); // 'idle' | 'booking' | 'success' | 'error'

  const resetBookingStatus = useCallback(() => {
    setBookingStatus('idle');
    setSessionOrderId(null);
    localStorage.removeItem('sessionOrderId');
  }, []);

  const updateSessionOrderId = useCallback((id) => {
    setSessionOrderId(id);
    if (id) {
      localStorage.setItem('sessionOrderId', id);
    } else {
      localStorage.removeItem('sessionOrderId');
    }
  }, []);

  useEffect(() => {
    if (sessionOrderId) {
      localStorage.setItem('sessionOrderId', sessionOrderId);
    } else {
      localStorage.removeItem('sessionOrderId');
    }
  }, [sessionOrderId]);

  const { activeStep, setActiveStep } = useSteps({
    index: (() => {
      const saved = localStorage.getItem('activeStep');
      return (saved && !isNaN(parseInt(saved))) ? parseInt(saved) : 1;
    })(),
    count: 5,
  });

  const cartPlansRef = useRef(cartPlans);
  useEffect(() => {
    cartPlansRef.current = cartPlans;
  }, [cartPlans]);

  // 1. FETCH FROM SERVER ON MOUNT + POLLING
  useEffect(() => {
    const fetchServerCart = async () => {
      if (user?._id) {
        try {
          const res = await axios.get(`${process.env.REACT_APP_SERVER}/cart/${user._id}`);
          if (res.data?.cart?.items) {
            const serverItems = res.data.cart.items;
            
            // Merge server items with local patient selections to prevent loss during sync lag
            const mergedItems = serverItems.map(sItem => {
              const localItem = cartPlansRef.current.find(lItem => lItem._id === sItem._id);
              if (localItem && localItem.patients?.length > 0 && (!sItem.patients || sItem.patients.length === 0)) {
                return { ...sItem, patients: localItem.patients };
              }
              return sItem;
            });

            skipSync.current = true;
            setCartPlans(mergedItems);
          } else {
            skipSync.current = true;
            setCartPlans([]);
          }
        } catch (error) {
          console.error("Error fetching server cart", error);
        }
      }
    };
    fetchServerCart();

    const interval = setInterval(fetchServerCart, 15000); // Poll every 15 seconds
    return () => clearInterval(interval);
  }, [user?._id]);

  // 2. SAVE TO LOCALSTORAGE AND SERVER ON CHANGE
  useEffect(() => {
    localStorage.setItem('cartPlans', JSON.stringify(cartPlans));
    localStorage.setItem('orderDetail', JSON.stringify(orderDetail));
    localStorage.setItem('activeStep', (activeStep || 1).toString());
    localStorage.setItem('orderSuccess', (orderSuccess || false).toString());
    localStorage.setItem('paymentStatus', paymentStatus || 'pending');

    const syncWithServer = async () => {
      if (user?._id && !skipSync.current) {
        try {
          await axios.post(`${process.env.REACT_APP_SERVER}/cart/${user._id}`, { items: cartPlans });
        } catch (error) {
          console.error("Error syncing cart to server", error);
        }
      }
      skipSync.current = false;
    };

    if (!isFirstMount.current) {
      syncWithServer();
    }
    isFirstMount.current = false;
  }, [cartPlans, orderDetail, activeStep, orderSuccess, paymentStatus, user?._id]);

  const addToCartPlans = (cartPlan) => {
    // If we had a previous successful order, reset for a new session
    if (orderSuccess || activeStep === 5) {
      setOrderSuccess(false);
      setPaymentStatus('pending');
      setActiveStep(1);
      setIsPaymentMethodConfirmed(false);
      setTriggerOrderPlacement(false);
      setOrderDetail(prev => ({
        ...prev,
        appointmentDate: '',
        appointmentTime: '',
      }));
      setSessionOrderId(null);
    }

    if (!cartPlans?.some(plan => plan._id === cartPlan._id)) {
      setCartPlans(prev => [...prev, cartPlan]);
    }
  };

  // Safety: If items are added to cart, ensure orderSuccess is false
  useEffect(() => {
    if (cartPlans?.length > 0 && orderSuccess) {
      setOrderSuccess(false);
      setPaymentStatus('pending');
      if (activeStep === 5) setActiveStep(1);
    }
  }, [cartPlans?.length, orderSuccess, activeStep, setActiveStep]);

  const removeFromCart = cartPlan => {
    const filteredCart = cartPlans?.filter(plan => plan._id !== cartPlan._id);
    setCartPlans(() => filteredCart);
  };

  const addPatient = useCallback(patients => {
    setPatients(patients);
  }, []);

  const getPatientsForAUser = useCallback(async () => {
    try {
      const res = await fetchPatient();
      setPatients(() => res);
    } catch (error) {
      toast({
        title: error?.message || 'Cannot fetch patients!',
        variant: 'subtle',
      });
    }
  }, [toast]);

  const addPatientToCartPlan = patient => {
    if (!selectedItem) return;

    // Check if patient already added to this item
    const isAlreadyAdded = selectedItem.patients?.some(p => p._id === patient._id);
    if (isAlreadyAdded) {
      toast({
        title: 'Patient already added',
        description: 'You can only add a patient once to the same test.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const updatedItem = {
      ...selectedItem,
      patients: [...(selectedItem.patients || []), patient]
    };

    setSelectedItem(updatedItem);

    setCartPlans(prevPlans => 
      prevPlans.map(plan => plan._id === selectedItem._id ? updatedItem : plan)
    );
  };

  const removePatientFromCartItem = (data, patient) => {
    const updatedItem = {
      ...data,
      patients: (data.patients || []).filter(p => p._id !== patient._id)
    };

    if (selectedItem?._id === data._id) {
      setSelectedItem(updatedItem);
    }

    setCartPlans(prevPlans =>
      prevPlans.map(plan => plan._id === data._id ? updatedItem : plan)
    );
  };

  const getAvailablePatients = () => {
    const available = (patients || []).filter(pat => {
      return !selectedItem?.patients?.some(sp => sp._id === pat._id);
    });
    setAvailablePatients(available);
  };

  useEffect(() => {
    const filteredCartItem = cartPlans.filter(
      plan => plan?.patients?.length > 0
    );
    setCartData(filteredCartItem);
  }, [cartPlans]);

  useEffect(() => {
    getPatientsForAUser();
  }, [getPatientsForAUser]);

  return (
    <CartContext.Provider
      value={{
        cartPlans,
        setCartPlans,
        addToCartPlans,
        patients,
        addPatient,
        availablePatients,
        getPatientsForAUser,
        removeFromCart,
        addPatientsDrawer,
        setAddPatientsDrawer,
        selectedItem,
        setSelectedItem,
        addPatientToCartPlan,
        removePatientFromCartItem,
        getAvailablePatients,
        location,
        setLocation,
        activeStep,
        setActiveStep,
        orderDetail,
        setOrderDetail,
        cartData,
        setCartData,
        isPaymentMethodConfirmed,
        setIsPaymentMethodConfirmed,
        triggerOrderPlacement,
        setTriggerOrderPlacement,
        orderSuccess,
        setOrderSuccess,
        paymentStatus,
        setPaymentStatus,
        sessionOrderId,
        setSessionOrderId: updateSessionOrderId,
        bookingStatus,
        setBookingStatus,
        resetBookingStatus,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
